import React, { useState, useMemo, useEffect } from 'react';
import { Search, MessageSquare, X, Filter, Lightbulb, Plus, Trash2 } from 'lucide-react';
import { useTheme } from '../ThemeContext';

interface TranscriptSegment {
    id: string;
    speaker: string;
    role?: string;
    text: string;
    timestamp?: string;
    isOperator?: boolean;
}

interface SmartTranscriptProps {
    rawText: string;
    companyName: string;
    quarter: string;
    year: string;
    sector?: string;
    onClose: () => void;
}

const SECTOR_KEYWORDS: Record<string, string[]> = {
    'Technology': ['AI', 'Cloud', 'SaaS', 'Data Center', 'Guidance', 'Margins', 'Subscription'],
    'Healthcare': ['FDA', 'Pipeline', 'Clinical', 'Approval', 'R&D', 'Patients'],
    'Financial Services': ['Interest Rates', 'Deposits', 'Credit', 'Loans', 'Net Interest Income', 'AUM'],
    'Energy': ['Oil', 'Gas', 'Production', 'Drilling', 'Prices', 'Capital Expenditure'],
    'Consumer Cyclical': ['Demand', 'Traffic', 'Inventory', 'Margins', 'Inflation', 'Same-Store Sales'],
    'Consumer Defensive': ['Volume', 'Pricing', 'Cost', 'Inflation', 'Organic Growth'],
    'Industrials': ['Backlog', 'Orders', 'Supply Chain', 'Aerospace', 'Defense'],
    'Real Estate': ['Occupancy', 'FFO', 'Leasing', 'Rent', 'Portfolio'],
    'Utilities': ['Regulatory', 'Grid', 'Renewables', 'Generation', 'Rates'],
    'Basic Materials': ['Commodity', 'Mining', 'Prices', 'Volume', 'Cost'],
    'Communication Services': ['Subscribers', 'ARPU', 'Advertising', 'Streaming', 'Churn'],
    'default': ['Revenue', 'EPS', 'Guidance', 'Growth', 'Margins', 'Cash Flow', 'Headwinds', 'Tailwinds']
};

export const SmartTranscript: React.FC<SmartTranscriptProps> = ({
    rawText,
    companyName,
    quarter,
    year,
    sector,
    onClose
}) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedSpeaker, setSelectedSpeaker] = useState<string>('All');
    const [keywords, setKeywords] = useState<string[]>([]);
    const [newKeyword, setNewKeyword] = useState('');
    const [showInsights, setShowInsights] = useState(true);

    // Initialize keywords based on sector
    useEffect(() => {
        const baseKeywords = sector && SECTOR_KEYWORDS[sector]
            ? SECTOR_KEYWORDS[sector]
            : SECTOR_KEYWORDS['default'];

        // Ensure 'Outlook' is always included for any sector
        const uniqueKeywords = Array.from(new Set([...baseKeywords, 'Outlook']));
        setKeywords(uniqueKeywords);
    }, [sector]);

    // Parse the raw text into segments
    const segments = useMemo(() => {
        const lines = rawText.split('\n');
        const parsedSegments: TranscriptSegment[] = [];
        let currentSpeaker = 'Unknown';
        let currentText: string[] = [];

        // Regex to identify speaker patterns like "Name: " or "Name Name: "
        const speakerRegex = /^([A-Z][a-zA-Z\.\s]+):(.*)/;

        lines.forEach((line) => {
            const match = line.match(speakerRegex);

            if (match) {
                // If we have accumulated text for the previous speaker, push it
                if (currentText.length > 0) {
                    parsedSegments.push({
                        id: `seg-${parsedSegments.length}`,
                        speaker: currentSpeaker,
                        text: currentText.join(' '),
                        isOperator: currentSpeaker.toLowerCase().includes('operator')
                    });
                    currentText = [];
                }

                // Start new speaker segment
                currentSpeaker = match[1].trim();
                if (match[2].trim()) {
                    currentText.push(match[2].trim());
                }
            } else {
                // Continue current speaker's text
                if (line.trim()) {
                    currentText.push(line.trim());
                }
            }
        });

        // Push the last segment
        if (currentText.length > 0) {
            parsedSegments.push({
                id: `seg-${parsedSegments.length}`,
                speaker: currentSpeaker,
                text: currentText.join(' '),
                isOperator: currentSpeaker.toLowerCase().includes('operator')
            });
        }

        return parsedSegments;
    }, [rawText]);

    // Calculate keyword counts
    const keywordCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        keywords.forEach(kw => {
            const regex = new RegExp(`\\b${kw}\\b`, 'gi');
            const count = (rawText.match(regex) || []).length;
            counts[kw] = count;
        });
        return counts;
    }, [keywords, rawText]);

    // Extract unique speakers for filter
    const speakers = useMemo(() => {
        const unique = new Set(segments.map(s => s.speaker));
        return ['All', ...Array.from(unique)];
    }, [segments]);

    // Filter segments based on search and speaker selection
    const filteredSegments = segments.filter(seg => {
        const matchesSearch = seg.text.toLowerCase().includes(searchQuery.toLowerCase()) ||
            seg.speaker.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesSpeaker = selectedSpeaker === 'All' || seg.speaker === selectedSpeaker;
        return matchesSearch && matchesSpeaker;
    });

    const handleAddKeyword = () => {
        if (newKeyword.trim() && !keywords.includes(newKeyword.trim())) {
            setKeywords([...keywords, newKeyword.trim()]);
            setNewKeyword('');
        }
    };

    const handleRemoveKeyword = (kw: string) => {
        setKeywords(keywords.filter(k => k !== kw));
    };

    // Helper to get initials
    const getInitials = (name: string) => {
        return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    };

    // Helper to get color for avatar
    const getAvatarColor = (name: string) => {
        if (name.toLowerCase().includes('operator')) return 'bg-gray-500';
        if (name.toLowerCase().includes('ceo') || name.toLowerCase().includes('cook')) return 'bg-blue-600';
        if (name.toLowerCase().includes('cfo')) return 'bg-green-600';

        const colors = [
            'bg-purple-600', 'bg-indigo-600', 'bg-pink-600', 'bg-orange-600',
            'bg-teal-600', 'bg-cyan-600', 'bg-rose-600'
        ];
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    };

    // Text highlighting logic
    const highlightText = (text: string) => {
        if (!searchQuery && keywords.length === 0) return text;

        let parts: (string | JSX.Element)[] = [text];

        // Highlight search query
        if (searchQuery) {
            const newParts: (string | JSX.Element)[] = [];
            parts.forEach(part => {
                if (typeof part === 'string') {
                    const split = part.split(new RegExp(`(${searchQuery})`, 'gi'));
                    split.forEach((s, i) => {
                        if (s.toLowerCase() === searchQuery.toLowerCase()) {
                            newParts.push(<span key={`search-${i}`} className="bg-yellow-200 text-yellow-900 px-0.5 rounded">{s}</span>);
                        } else {
                            newParts.push(s);
                        }
                    });
                } else {
                    newParts.push(part);
                }
            });
            parts = newParts;
        }

        // Highlight keywords
        keywords.forEach(kw => {
            const newParts: (string | JSX.Element)[] = [];
            parts.forEach(part => {
                if (typeof part === 'string') {
                    const split = part.split(new RegExp(`\\b(${kw})\\b`, 'gi'));
                    split.forEach((s, i) => {
                        if (s.toLowerCase() === kw.toLowerCase()) {
                            newParts.push(<span key={`kw-${kw}-${i}`} className="bg-blue-100 text-blue-800 px-0.5 rounded font-medium border border-blue-200">{s}</span>);
                        } else {
                            newParts.push(s);
                        }
                    });
                } else {
                    newParts.push(part);
                }
            });
            parts = newParts;
        });

        return parts.map((part, i) => <React.Fragment key={i}>{part}</React.Fragment>);
    };

    return (
        <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 ${isDark ? 'bg-black/80' : 'bg-gray-900/50'} backdrop-blur-sm`}>
            <div className={`relative w-full max-w-6xl h-[85vh] flex rounded-2xl shadow-2xl overflow-hidden ${isDark ? 'bg-theme-secondary border border-theme-tertiary' : 'bg-white'}`}>

                {/* Main Content Area */}
                <div className="flex-1 flex flex-col min-w-0">
                    {/* Header */}
                    <div className={`px-6 py-4 border-b flex items-center justify-between ${isDark ? 'border-theme-tertiary bg-theme-primary' : 'border-gray-100 bg-gray-50'}`}>
                        <div>
                            <h2 className={`text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                <MessageSquare size={20} className="text-blue-500" />
                                Earnings Transcript
                            </h2>
                            <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {companyName} • {quarter} {year}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setShowInsights(!showInsights)}
                                className={`p-2 rounded-lg transition-colors flex items-center gap-2 text-sm font-medium ${showInsights
                                    ? (isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-50 text-blue-600')
                                    : (isDark ? 'hover:bg-theme-secondary text-gray-400' : 'hover:bg-gray-200 text-gray-500')
                                    }`}
                            >
                                <Lightbulb size={18} />
                                <span className="hidden sm:inline">Key Insights</span>
                            </button>

                            <button
                                onClick={onClose}
                                className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-red-900/30 text-gray-400 hover:text-red-400' : 'hover:bg-red-50 text-gray-500 hover:text-red-600'}`}
                            >
                                <X size={20} />
                            </button>
                        </div>
                    </div>

                    {/* Controls (Desktop Only) */}
                    <div className={`hidden lg:flex px-6 py-3 border-b flex-col sm:flex-row gap-3 ${isDark ? 'border-theme-tertiary bg-theme-secondary' : 'border-gray-100 bg-white'}`}>
                        <div className={`relative flex-1`}>
                            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} size={16} />
                            <input
                                type="text"
                                placeholder="Search transcript..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className={`w-full pl-9 pr-4 py-2 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDark
                                    ? 'bg-theme-primary border-theme-tertiary text-white placeholder-gray-600'
                                    : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                                    }`}
                            />
                        </div>
                        <div className="flex items-center gap-2 min-w-[200px]">
                            <Filter size={16} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                            <select
                                value={selectedSpeaker}
                                onChange={(e) => setSelectedSpeaker(e.target.value)}
                                className={`flex-1 px-3 py-2 rounded-lg text-sm border focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDark
                                    ? 'bg-theme-primary border-theme-tertiary text-white'
                                    : 'bg-gray-50 border-gray-200 text-gray-900'
                                    }`}
                            >
                                {speakers.map(speaker => (
                                    <option key={speaker} value={speaker}>{speaker}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Mobile Key Insights (Wrapped Chips & Management) */}
                    {showInsights && (
                        <div className={`lg:hidden flex flex-col border-b ${isDark ? 'border-theme-tertiary bg-theme-secondary/50' : 'border-gray-100 bg-gray-50/50'}`}>
                            {/* Mobile Toolbar: Add Keyword + Speaker Filter */}
                            <div className={`px-4 py-2 flex items-center gap-2 border-b ${isDark ? 'border-theme-tertiary' : 'border-gray-100/50'}`}>
                                {/* Add Keyword */}
                                <div className="flex-1 flex gap-2">
                                    <input
                                        type="text"
                                        placeholder="Add keyword..."
                                        value={newKeyword}
                                        onChange={(e) => setNewKeyword(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleAddKeyword()}
                                        className={`w-full px-3 py-1.5 text-xs rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDark
                                            ? 'bg-theme-secondary border-theme-tertiary text-white placeholder-gray-500'
                                            : 'bg-white border-gray-300 text-gray-900'
                                            }`}
                                    />
                                    <button
                                        onClick={handleAddKeyword}
                                        className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${isDark ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'
                                            }`}
                                    >
                                        <Plus size={14} />
                                    </button>
                                </div>

                                {/* Divider */}
                                <div className={`w-px h-6 mx-1 ${isDark ? 'bg-gray-700' : 'bg-gray-300'}`}></div>

                                {/* Speaker Filter */}
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    <Filter size={14} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                                    <select
                                        value={selectedSpeaker}
                                        onChange={(e) => setSelectedSpeaker(e.target.value)}
                                        className={`w-24 px-2 py-1.5 rounded-lg text-xs border focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDark
                                            ? 'bg-theme-secondary border-theme-tertiary text-white'
                                            : 'bg-white border-gray-300 text-gray-900'
                                            }`}
                                    >
                                        {speakers.map(speaker => (
                                            <option key={speaker} value={speaker}>{speaker}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Mobile Chips List */}
                            <div className="px-6 py-3 flex flex-wrap items-center gap-2">
                                <div className="flex items-center gap-2 pr-2">
                                    <Lightbulb size={16} className="text-yellow-500 flex-shrink-0" />
                                    <span className={`text-xs font-medium whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        Insights:
                                    </span>
                                </div>
                                {keywords.map(kw => {
                                    const isActive = searchQuery.toLowerCase() === kw.toLowerCase();
                                    return (
                                        <div
                                            key={kw}
                                            className={`flex items-center rounded-full text-xs font-medium transition-colors border ${isActive
                                                ? (isDark ? 'bg-blue-600 border-blue-500 text-white' : 'bg-blue-100 border-blue-200 text-blue-800')
                                                : keywordCounts[kw] > 0
                                                    ? (isDark ? 'bg-blue-900/30 border-blue-800 text-blue-200' : 'bg-white border-gray-200 text-gray-700')
                                                    : (isDark ? 'bg-theme-tertiary border-theme-tertiary text-gray-500' : 'bg-gray-100 border-gray-200 text-gray-400')
                                                }`}
                                        >
                                            <button
                                                onClick={() => setSearchQuery(isActive ? '' : kw)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-l-full hover:opacity-80"
                                            >
                                                {kw}
                                                {keywordCounts[kw] > 0 && (
                                                    <span className={`px-1 rounded-full text-[10px] ${isActive
                                                        ? (isDark ? 'bg-white/20' : 'bg-blue-200')
                                                        : (isDark ? 'bg-blue-900/50' : 'bg-gray-100')
                                                        }`}>
                                                        {keywordCounts[kw]}
                                                    </span>
                                                )}
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveKeyword(kw);
                                                }}
                                                className={`pr-2 pl-1 py-1.5 rounded-r-full hover:text-red-500 transition-colors ${isActive ? 'text-blue-200 hover:text-white' : 'text-gray-400'
                                                    }`}
                                            >
                                                <X size={12} />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Transcript Content */}
                    <div className={`flex-1 overflow-y-auto p-6 space-y-6 scrollbar-thin ${isDark ? 'scrollbar-thumb-gray-700 scrollbar-track-transparent' : 'scrollbar-thumb-gray-300'}`}>
                        {filteredSegments.length > 0 ? (
                            filteredSegments.map((seg) => (
                                <div key={seg.id} className={`flex gap-4 group ${seg.isOperator ? 'opacity-75' : ''}`}>
                                    <div className="flex-shrink-0 mt-1">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm ${getAvatarColor(seg.speaker)}`}>
                                            {getInitials(seg.speaker)}
                                        </div>
                                    </div>
                                    <div className="flex-1 space-y-1">
                                        <div className="flex items-baseline justify-between">
                                            <h3 className={`text-sm font-bold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                                                {seg.speaker}
                                            </h3>
                                            {/* <span className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>00:00</span> */}
                                        </div>
                                        <div className={`text-sm leading-relaxed whitespace-pre-wrap ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                            {highlightText(seg.text)}
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full opacity-50">
                                <Search size={48} className="mb-4" />
                                <p>No results found for "{searchQuery}"</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Key Insights Sidebar (Desktop Only) */}
                {showInsights && (
                    <div className={`
                        hidden lg:flex lg:w-80 lg:border-l lg:flex-col
                        ${isDark ? 'bg-theme-primary border-theme-tertiary' : 'bg-gray-50 border-gray-200'}
                    `}>
                        <div className={`p-4 border-b flex items-center justify-between ${isDark ? 'border-theme-tertiary' : 'border-gray-200'}`}>
                            <div>
                                <h3 className={`font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    <Lightbulb size={18} className="text-yellow-500" />
                                    Key Insights
                                </h3>
                                <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Tracking key topics for {sector || 'this company'}
                                </p>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                            {/* Add Keyword Input */}
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    placeholder="Add keyword..."
                                    value={newKeyword}
                                    onChange={(e) => setNewKeyword(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleAddKeyword()}
                                    className={`flex-1 px-3 py-1.5 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDark
                                        ? 'bg-theme-secondary border-theme-tertiary text-white placeholder-gray-500'
                                        : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                />
                                <button
                                    onClick={handleAddKeyword}
                                    className={`p-1.5 rounded-lg transition-colors ${isDark ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'
                                        }`}
                                >
                                    <Plus size={18} />
                                </button>
                            </div>

                            {/* Keywords List */}
                            <div className="space-y-2">
                                {keywords.map(kw => {
                                    const isActive = searchQuery.toLowerCase() === kw.toLowerCase();
                                    return (
                                        <div
                                            key={kw}
                                            onClick={() => setSearchQuery(isActive ? '' : kw)}
                                            className={`flex items-center justify-between p-2 rounded-lg border cursor-pointer transition-all ${isActive
                                                ? (isDark ? 'bg-blue-600 border-blue-500 ring-2 ring-blue-400/30' : 'bg-blue-100 border-blue-300 ring-2 ring-blue-400/30')
                                                : keywordCounts[kw] > 0
                                                    ? (isDark ? 'bg-blue-900/20 border-blue-800 hover:bg-blue-900/40' : 'bg-blue-50 border-blue-100 hover:bg-blue-100')
                                                    : (isDark ? 'bg-theme-secondary border-theme-tertiary opacity-60' : 'bg-white border-gray-200 opacity-60')
                                                }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className={`text-sm font-medium ${isActive
                                                    ? (isDark ? 'text-white' : 'text-blue-900')
                                                    : (isDark ? 'text-gray-200' : 'text-gray-700')
                                                    }`}>
                                                    {kw}
                                                </span>
                                                {keywordCounts[kw] > 0 && (
                                                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive
                                                        ? (isDark ? 'bg-white/20 text-white' : 'bg-blue-200 text-blue-800')
                                                        : (isDark ? 'bg-blue-800 text-blue-200' : 'bg-blue-100 text-blue-700')
                                                        }`}>
                                                        {keywordCounts[kw]}
                                                    </span>
                                                )}
                                            </div>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveKeyword(kw);
                                                }}
                                                className={`p-1 rounded transition-colors ${isActive
                                                    ? (isDark ? 'hover:bg-white/20 text-blue-100' : 'hover:bg-blue-200 text-blue-700')
                                                    : 'hover:bg-red-100 hover:text-red-500 text-gray-400'
                                                    }`}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
